import {
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  Heading,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { mdiOfficeBuildingOutline } from "@mdi/js";
import { AuraSelect } from "@questy/aura-cl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { FaTrash } from "react-icons/fa";
import {
  addNewCustomOfferPrice,
  customOfferPriceEditor,
  getAutoselectCommissionByQuery,
  getDetailService,
} from "./api";
import { useViewOfferPriceListContext } from "./context";
import { id } from "date-fns/locale";

export const CustomViewOfferPriceListAddNew = () => {
  const { refreshData } = useViewOfferPriceListContext();
  const form = useForm();
  const modal = useDisclosure({
    onClose: () => {
      form.reset({ company: null });
    },
  });
  const isLoading = useDisclosure();
  const [commissions, setCommissions] = useState<any[]>([]);

  useEffect(() => {
    const company = form.watch("company")?.value;
    const service = form.watch("service")?.value;
    if (company)
      if (service)
        getDetailService(service, company)
          .then((e) => e.json())
          .then((e) => {
            form.setValue("positions", e?.recipe);
          });
  }, [form.watch("service")]);

  // czy usługa została wybrana
  const selectedService = useMemo(() => {
    return form.watch("service") != null;
  }, [form.watch("service")]);

  // zapisanie cennika
  const saveHandle = useCallback(() => {
    const data = form.getValues();
    isLoading.onOpen();

    // async for
    new Promise(async (res, rej) => {
      for (const e of data?.positions) {
        await addNewCustomOfferPrice({
          service: data?.service?.value,
          price: e.price,
          projectName: data?.project_name,
          realizationNumber: data?.number_realization,
          accountCompany: data.company.value,
          description: e?.description,
          product: e?.id,
          startDate: data?.date_start,
          endDate: data?.date_end,
          commission: data?.commission?.value,
        });
      }
      res({});
    })
      .then(() => {
        modal.onClose();
        isLoading.onClose();
        refreshData();
      })
      .catch(() => {
        isLoading.onClose();
      });
  }, [form.getValues]);

  return (
    <>
      <Button
        colorScheme={"teal"}
        onClick={() => {
          modal.onOpen();
        }}
      >
        Dodaj cennik
      </Button>
      <Modal isOpen={modal.isOpen} onClose={modal.onClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Dodaj cennik</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDirection={"column"} gap="10px">
              <FormProvider {...form}>
                <Flex flexDirection={"column"}>
                  <FormLabel>Nazwa klienta</FormLabel>
                  <AuraSelect
                    inputId="company"
                    isClearable={false}
                    isReadOnly={false}
                    loadDataProps={{
                      endpoint: "account_companies",
                      labelField: "displayName",
                      autoselect: true,
                      settings: { extra_fields: "displayName" },
                    }}
                    icon={mdiOfficeBuildingOutline}
                    blurInputOnSelect={true}
                    placeholder="Nazwa klienta"
                    onChange={(value) => {
                      form.setValue("company", value);
                    }}
                  ></AuraSelect>
                </Flex>

                <Flex flexDirection={"column"}>
                  <FormLabel>Nazwa projektu</FormLabel>
                  <Textarea
                    {...form.register("project_name")}
                    placeholder="Nazwa projektu"
                  />
                </Flex>

                <Flex flexDirection={"column"}>
                  <FormLabel>Proces</FormLabel>
                  <AuraSelect
                    inputId="commission"
                    isClearable={false}
                    isReadOnly={false}
                    options={commissions}
                    onInputChange={(value) => {
                      if (value.length < 1) return;
                      getAutoselectCommissionByQuery(value)
                        .then((e) => e.json())
                        .then((e) => {
                          const data = e?.data;
                          if (data) {
                            setCommissions(
                              data?.map((e: any) => ({
                                value: e?.id,
                                label: `${e?.commissionNumber} ${
                                  e?.name ? `[${e?.name}]` : ""
                                }`,
                                attributes: e?.attributes,
                              }))
                            );
                          }
                        });
                    }}
                    blurInputOnSelect={true}
                    placeholder="Proces"
                    onChange={(value, meta) => {
                      form.setValue("commission", value);
                      const attribute = value?.attributes.find(
                        (e: any) => e?.id === 202
                      );
                      if (attribute)
                        form.setValue("number_realization", attribute?.value);
                    }}
                  ></AuraSelect>
                </Flex>

                <Flex flexDirection={"column"}>
                  <FormLabel>Numer realizacyjny</FormLabel>
                  <Textarea
                    {...form.register("number_realization")}
                    placeholder="Numer realizacyjny"
                  />
                </Flex>

                <Flex flexDirection={"column"}>
                  <FormLabel>Usługa</FormLabel>
                  <AuraSelect
                    inputId="service"
                    isClearable={false}
                    isReadOnly={form.watch("company") == null}
                    loadDataProps={{
                      endpoint: "products",
                      labelField: "fullName",
                      autoselect: true,
                      settings: {
                        "filter[isService]": true,
                        fields: "isService",
                        extra_fields: "fullName",
                      },
                    }}
                    blurInputOnSelect={true}
                    placeholder="Wybierz usługę"
                    onChange={(value) => {
                      form.setValue("service", value);
                    }}
                  ></AuraSelect>
                </Flex>

                <Flex
                  flexDirection={"column"}
                  display={selectedService ? "block" : "none"}
                  mt={"20px"}
                >
                  <Controller
                    name="positions"
                    render={({ field }) => {
                      return (
                        <Grid>
                          <Grid
                            gridTemplateColumns={"3fr 7fr 2fr 1fr"}
                            fontWeight="bold"
                            gap={"10px"}
                          >
                            <Box>Nazwa</Box>
                            <Box>Opis</Box>
                            <Box>Cena</Box>
                            <Box>Akcje</Box>
                          </Grid>
                          <Flex gap={"20px"} flexDirection="column" mt={"20px"}>
                            {field.value?.map((e: any, i: number) => (
                              <Grid
                                key={i}
                                gridTemplateColumns={"3fr 7fr 2fr 1fr"}
                                gap={"10px"}
                              >
                                <Text fontWeight={"bold"}>{e?.name}</Text>
                                <Textarea
                                  {...form.register(
                                    `positions.${i}.description`
                                  )}
                                ></Textarea>
                                <Input
                                  type={"number"}
                                  {...form.register(`positions.${i}.price`)}
                                ></Input>
                                <Box>
                                  <IconButton
                                    colorScheme="red"
                                    aria-label="Delete"
                                    onClick={() => {
                                      form.setValue("positions", [
                                        ...field.value.slice(0, i),
                                        ...field.value.slice(i + 1),
                                      ]);
                                    }}
                                    size="md"
                                    icon={<FaTrash />}
                                  />
                                </Box>
                              </Grid>
                            ))}
                          </Flex>
                        </Grid>
                      );
                    }}
                  />
                </Flex>

                <Grid gridTemplateColumns={"1fr 1fr"} gap="20px" mt={"20px"}>
                  <Flex flexDirection={"column"}>
                    <FormLabel>Data rozpoczącia</FormLabel>
                    <Input {...form.register("date_start")} type={"date"} />
                  </Flex>

                  <Flex flexDirection={"column"}>
                    <FormLabel>Data zakończenia</FormLabel>
                    <Input {...form.register("date_end")} type={"date"} />
                  </Flex>
                </Grid>
              </FormProvider>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button
              isLoading={isLoading.isOpen}
              colorScheme={"teal"}
              onClick={saveHandle}
            >
              Zapisz
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
